/**
 * Pure projections from the event log to objective/action read models
 * (`ObjectiveView`, `ObjectiveSummaryView`, `ActionView`, `MilestoneGroupView`
 * -- domain-api.ts). No I/O; `today` is only used to compute the embedded
 * forecast (ObjectiveForecastView is a function of (log, today), per its
 * contract doc comment).
 *
 * Phase 5 additions (ADR-0002 addendum 2026-07-04): `state` widens to the
 * full `ObjectiveStatus`; `ActionView` gains `status`/`effortMinutes`;
 * `ObjectiveView` gains `linkedObjectives`, the organizational fields
 * (category/effort/priority/reminder/cadence goal/visibility toggles), and
 * `totalEffortMinutes`.
 */

import type {
  ActionView,
  CompassEvent,
  MilestoneGroupView,
  ObjectiveId,
  ObjectiveSummaryView,
  ObjectiveView,
  PinnedObjectiveView,
} from '../../contracts';
import { computeObjectiveForecast } from '../forecast/objective-forecast';
import { computeWeekOutcome } from '../habits/logging';
import { buildHabitStates, weeklyPlannedFor } from '../habits/state';
import { weekOf } from '../habits/week';
import {
  buildObjectiveStates,
  computeActionCompletionStatus,
  computeActionDisplayStatus,
  resolveLinkedObjectiveIds,
  type ObjectiveState,
} from './state';

/**
 * Build the full `ObjectiveView` for one objective, or `null` if it does not
 * exist in the log.
 */
export function projectObjectiveView(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  today: string,
): ObjectiveView | null {
  const states = buildObjectiveStates(events);
  const state = states.get(objectiveId);
  if (!state) return null;

  const completion = computeActionCompletionStatus(events);
  const displayStatus = computeActionDisplayStatus(events, completion);
  const actions = buildActionViews(state, completion, displayStatus);
  const milestoneGroups = groupByMilestoneTag(actions);
  const progress = {
    done: actions.filter((a) => a.completed).length,
    total: actions.length,
  };
  const forecast = computeObjectiveForecast(events, objectiveId, today);
  const totalEffortMinutes = actions.reduce((sum, a) => sum + (a.effortMinutes ?? 0), 0);

  return {
    objectiveId: state.objectiveId,
    title: state.title,
    why: state.why,
    targetDate: state.targetDate as ObjectiveView['targetDate'],
    state: state.status,
    actions,
    milestoneGroups,
    progress,
    forecast,
    linkedHabits: buildLinkedHabits(events, objectiveId, today),
    linkedObjectives: buildLinkedObjectives(events, states, objectiveId),
    category: state.category,
    levelOfEffort: state.levelOfEffort,
    priority: state.priority,
    reminder: state.reminder,
    cadenceGoalPerWeek: state.cadenceGoalPerWeek,
    includeInWeeklyReview: state.includeInWeeklyReview,
    surfaceOnToday: state.surfaceOnToday,
    ambientRewardOnComplete: state.ambientRewardOnComplete,
    totalEffortMinutes,
    pinned: state.pinned,
    pinnedOrder: state.pinnedOrder,
  };
}

/**
 * `ObjectiveView.linkedHabits` (R3.6, phase-3 spec §2 HL-2): every
 * active-or-paused habit whose `objectiveId` matches this objective, each
 * with its current-week `WeekOutcome` -- the SAME per-habit computation the
 * weekly review already calls (`computeWeekOutcome`, `../habits/logging.ts`),
 * not a second, independently-derived adherence number. Archived habits are
 * excluded so archiving one quietly drops it from this list on the next read
 * without touching the habit's own `objectiveId` or the objective's action
 * progress (HL-4) -- this mirrors the existing `state === 'active'`
 * filtering pattern `computeDueHabits`/`buildHabitWeekReports` already use
 * elsewhere, just relaxed to include 'paused' too (a paused habit still
 * "belongs" to the objective; it simply isn't due today, same distinction
 * `buildHabitStates`'s lifecycle field already draws).
 */
function buildLinkedHabits(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  today: string,
): ObjectiveView['linkedHabits'] {
  const habitStates = buildHabitStates(events);
  const weekStart = weekOf(today);

  const linked: Array<ObjectiveView['linkedHabits'][number]> = [];
  for (const habit of habitStates.values()) {
    if (habit.objectiveId !== objectiveId) continue;
    if (habit.lifecycle === 'archived') continue;

    const planned = weeklyPlannedFor(habit.plan);
    const currentWeek = computeWeekOutcome(
      events,
      habit.habitId,
      weekStart,
      habit.plan.habitKind,
      planned,
    );

    linked.push({
      habitId: habit.habitId,
      name: habit.name,
      currentWeek,
    });
  }

  return linked.sort((a, b) => (a.habitId < b.habitId ? -1 : a.habitId > b.habitId ? 1 : 0));
}

/**
 * `ObjectiveView.linkedObjectives` (R3.13, Phase 5, ADR-0002 addendum
 * 2026-07-04 D4) -- symmetric, purely informational, no rollup math (AC-O19).
 * Reads `resolveLinkedObjectiveIds` (the count-parity resolver) and projects
 * each linked ID's current title from the ALREADY-BUILT `states` map (no
 * second `buildObjectiveStates` pass) -- a linked objective that has since
 * been archived still appears here (linking carries no lifecycle
 * consequence, matching `linkedHabits`'s own "still belongs" precedent for
 * paused habits); a linked ID with NO state at all (should not happen in
 * practice, since objectiveIds are never fabricated) is defensively skipped.
 */
function buildLinkedObjectives(
  events: readonly CompassEvent[],
  states: ReadonlyMap<ObjectiveId, ObjectiveState>,
  objectiveId: ObjectiveId,
): ObjectiveView['linkedObjectives'] {
  const linkedIds = resolveLinkedObjectiveIds(events, objectiveId);
  const linked: Array<{ readonly objectiveId: ObjectiveId; readonly title: string }> = [];
  for (const linkedId of linkedIds) {
    const linkedState = states.get(linkedId);
    if (!linkedState) continue;
    linked.push({ objectiveId: linkedId, title: linkedState.title });
  }
  return linked.sort((a, b) => (a.objectiveId < b.objectiveId ? -1 : a.objectiveId > b.objectiveId ? 1 : 0));
}

/** Compact `ObjectiveSummaryView` list for every non-removed objective. */
export function projectObjectiveSummaries(
  events: readonly CompassEvent[],
  today: string,
  options?: { readonly includeArchived?: boolean },
): readonly ObjectiveSummaryView[] {
  const states = buildObjectiveStates(events);
  const completion = computeActionCompletionStatus(events);
  const displayStatus = computeActionDisplayStatus(events, completion);
  const includeArchived = options?.includeArchived ?? false;

  const summaries: ObjectiveSummaryView[] = [];
  for (const state of states.values()) {
    if (state.status === 'archived' && !includeArchived) continue;
    const actions = buildActionViews(state, completion, displayStatus);
    const forecast = computeObjectiveForecast(events, state.objectiveId, today);
    summaries.push({
      objectiveId: state.objectiveId,
      title: state.title,
      targetDate: state.targetDate as ObjectiveSummaryView['targetDate'],
      progress: {
        done: actions.filter((a) => a.completed).length,
        total: actions.length,
      },
      isSlipping: forecast.isSlipping,
      state: state.status,
      archivedEventId: state.archivedEventId,
      category: state.category,
    });
  }
  // Creation order falls out of UUIDv7 entity IDs (ADR-0003); sort by
  // objectiveId for a stable, deterministic listing order.
  return summaries.sort((a, b) =>
    a.objectiveId < b.objectiveId ? -1 : a.objectiveId > b.objectiveId ? 1 : 0,
  );
}

/**
 * The sidebar's PINNED OBJECTIVES quick-jump list (R8.8, Phase 6) -- only
 * `pinned === true` objectives, sorted by `pinnedOrder`. A thin, dedicated
 * shape (colored dot + name only, no progress/forecast/state) mirroring
 * `DueActionView`'s own "thin list view, full detail elsewhere" precedent.
 * Archived-but-still-pinned objectives are excluded (mirrors every other
 * "archived drops out of active surfaces" rule in this file) -- pinning an
 * objective and later archiving it should not leave a dead link in the
 * sidebar; the pin state itself is left untouched by archiving (no event is
 * appended here), so un-archiving restores the pin with no further action.
 */
export function projectPinnedObjectives(
  events: readonly CompassEvent[],
): readonly PinnedObjectiveView[] {
  const states = buildObjectiveStates(events);
  const pinned: PinnedObjectiveView[] = [];
  for (const state of states.values()) {
    if (!state.pinned || state.status === 'archived') continue;
    pinned.push({
      objectiveId: state.objectiveId,
      title: state.title,
      category: state.category,
      // Bug-fix note (qa-engineer sweep, AC-T20): this comment previously
      // claimed pinnedOrder is "guaranteed non-null whenever pinned is
      // true... never expected to trigger" -- that was never true.
      // `setObjectivePinned` passes the caller's pinnedOrder straight
      // through when pinned=true (commands.ts: `pinned ? pinnedOrder :
      // null`), and the only shipped caller
      // (ObjectiveFormScreen.desktop.tsx's Pinned-to-Today toggle) always
      // sends `null` -- there is currently no UI that ever sets a real
      // order. The `?? 0` fallback below is therefore the PRIMARY path in
      // practice, not a defensive one: every pinned objective ties at
      // order 0 and the stable sort below falls back to insertion order.
      // A real order value requires a reorder control, which
      // design/screens/today-desktop-v2.md's own §3 explicitly flags as an
      // "implied downstream need... not specified by this document" --
      // i.e. a known, disclosed scope boundary, not a silent gap.
      pinnedOrder: state.pinnedOrder ?? 0,
    });
  }
  return pinned.sort((a, b) => a.pinnedOrder - b.pinnedOrder);
}

function buildActionViews(
  state: ObjectiveState,
  completion: ReturnType<typeof computeActionCompletionStatus>,
  displayStatus: ReturnType<typeof computeActionDisplayStatus>,
): readonly ActionView[] {
  const views: ActionView[] = [];
  for (const actionId of state.order) {
    const action = state.actionsById.get(actionId);
    if (!action || action.removed) continue;
    const status = completion.get(actionId);
    views.push({
      actionId: action.actionId,
      title: action.title,
      dueDate: action.dueDate as ActionView['dueDate'],
      milestoneTag: action.milestoneTag,
      completed: status !== undefined,
      completedOn: (status?.completedOn ?? null) as ActionView['completedOn'],
      entryEventId: (status?.entryEventId ?? null) as ActionView['entryEventId'],
      status: displayStatus.get(actionId) ?? 'to_do',
      effortMinutes: action.effortMinutes,
    });
  }
  return views;
}

/**
 * Group actions by `milestoneTag` in first-seen tag order (per the contract
 * doc comment on `MilestoneGroupView`); actions with `milestoneTag: null`
 * collect under `tag: null`. Pure presentation derivation -- never stored.
 */
export function groupByMilestoneTag(
  actions: readonly ActionView[],
): readonly MilestoneGroupView[] {
  const order: Array<string | null> = [];
  const byTag = new Map<string | null, ActionView[]>();

  for (const action of actions) {
    const tag = action.milestoneTag;
    if (!byTag.has(tag)) {
      byTag.set(tag, []);
      order.push(tag);
    }
    byTag.get(tag)?.push(action);
  }

  return order.map((tag) => {
    const groupActions = byTag.get(tag) ?? [];
    return {
      tag,
      actions: groupActions,
      done: groupActions.filter((a) => a.completed).length,
      total: groupActions.length,
    };
  });
}
